<template>
  <v-container class="mt-0 pt-0">
    <h2 class="ml-4">
      Export to platform
      <span class="success--text">{{ platform.platform }}</span>
    </h2>

    <v-stepper v-model="stepper">
      <v-stepper-header>
        <v-stepper-step :complete="stepper > 1" step="1" color="accent">
          Fix products
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step :complete="stepper > 2" step="2" color="accent">
          Review products
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="3" color="accent">
          Translate words
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="4" color="accent">
          Map data
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="4.5" color="accent">
          Fill missing fields
        </v-stepper-step>

        <v-divider></v-divider>

        <v-stepper-step step="5" color="accent">
          Upload
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1: Fix products -->
        <v-stepper-content step="1">
          <ProductsToFix
            v-if="productsToFix"
            :products="products"
            @getProductsToFix="getProductsForUpload(false)"
            @getProductsForUpload="getProductsForUpload(true)"
          ></ProductsToFix>

          <div v-if="loadingProducts" class="mt-3">
            <div class="text-center">
              <v-progress-circular indeterminate size="100" width="8"></v-progress-circular>
            </div>
            <v-row justify="center" class="mt-8">
              <v-col cols="2" class="text-center">
                <p class="basic--text">Getting products... Please wait...</p>
              </v-col>
            </v-row>
          </div>

          <div v-if="getProductsFailed">
            <v-row class="pb-2" justify="center">
              <v-img :src="require(`@/assets/${responseImg}`)" max-width="300"></v-img>
            </v-row>
            <v-row class="pb-2 text-center" justify="center">
              <h3 class="basic--text">{{ responseMessage }}</h3>
            </v-row>
          </div>
        </v-stepper-content>

        <!-- Step 2: Review products -->
        <v-stepper-content step="2">
          <div v-if="loadingProducts" class="mt-3">
            <div class="text-center">
              <v-progress-circular indeterminate size="100" width="8"></v-progress-circular>
            </div>
            <v-row justify="center" class="mt-8">
              <v-col cols="2" class="text-center">
                <p class="basic--text">Getting products... Please wait...</p>
              </v-col>
            </v-row>
          </div>

          <div v-if="getProductsFailed">
            <v-row class="pb-2" justify="center">
              <v-img :src="require(`@/assets/${responseImg}`)" max-width="300"></v-img>
            </v-row>
            <v-row class="pb-2 text-center" justify="center">
              <h3 class="basic--text">{{ responseMessage }}</h3>
            </v-row>
          </div>

          <ProductsToUpload
            v-if="productsToUpload"
            :products="products"
            @getProductsToFix="getProductsForUpload(false)"
            @goToTranslations="getProductsToTranslate"
          ></ProductsToUpload>
        </v-stepper-content>

        <!-- Step 3: Translate words -->
        <v-stepper-content step="3">
          <ProductsToTranslate
            v-if="productsToTranslate"
            :skus="SKUToUpload"
            @getProductsForUpload="getProductsForUpload(true)"
            @translatedSuccessfully="translatedSuccessfully"
            :platform="platform"
          ></ProductsToTranslate>
        </v-stepper-content>

        <!-- Step 4: Map data -->
        <v-stepper-content step="4">
          <CategoryMapping
            v-if="showCategoryMapping"
            :platform="platform"
            :skus="SKUToUpload"
            @getTranslations="getProductsToTranslate"
            @goToUpload="goToAspectCheck"
          ></CategoryMapping>
        </v-stepper-content>

        <!-- Step 4.5: Fill missing eBay aspects -->
        <v-stepper-content step="4.5">
          <div v-if="checkingAspects" class="text-center mt-6">
            <v-progress-circular indeterminate size="80" width="6"></v-progress-circular>
            <p class="mt-4 basic--text">Checking required eBay fields...</p>
          </div>

          <FillAspects
            v-if="showFillAspects"
            :report="aspectsReport"
            :skus="SKUToUpload"
            :platform="platform"
            @goBack="stepper = 4"
            @confirmed="onAspectsConfirmed"
          />
        </v-stepper-content>

        <!-- Step 5: Upload results -->
        <v-stepper-content step="5">
          <!-- Loading state -->
          <div v-if="uploading">
            <v-row justify="center">
              <v-col cols="auto">
                <v-img src="@/assets/loading.svg" max-width="300"></v-img>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="auto">
                <h2>Uploading products, please wait...</h2>
              </v-col>
            </v-row>
          </div>

          <!-- Results state -->
          <div v-if="uploadResults && !uploading">
            <!-- Header -->
            <v-row justify="center" class="mb-4">
              <v-col cols="auto" class="text-center">
                <v-icon size="64" color="success" v-if="uploadResults.failed === 0">
                  mdi-check-circle
                </v-icon>
                <v-icon size="64" color="warning" v-else>
                  mdi-alert-circle
                </v-icon>
                <h2 class="mt-2">
                  Upload complete —
                  <span class="success--text">{{ uploadResults.success }} succeeded</span>
                  <span v-if="uploadResults.failed > 0">
                    , <span class="error--text">{{ uploadResults.failed }} failed</span>
                  </span>
                </h2>
              </v-col>
            </v-row>

            <!-- Success table -->
            <div v-if="uploadResults.details.success.length > 0">
              <h3 class="ml-2 mb-2 success--text">
                <v-icon color="success">mdi-check</v-icon>
                Successfully uploaded
              </h3>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Listing ID</th>
                      <th>eBay Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in uploadResults.details.success" :key="item.sku">
                      <td>{{ item.sku }}</td>
                      <td>{{ item.listingId }}</td>
                      <td>
                        <a
                          :href="`https://${platform.platform}/itm/${item.listingId}`"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <v-icon small color="accent">mdi-open-in-new</v-icon>
                          View on eBay
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Failed table -->
            <div v-if="uploadResults.details.failed.length > 0" class="mt-6">
              <h3 class="ml-2 mb-2 error--text">
                <v-icon color="error">mdi-close</v-icon>
                Failed uploads
              </h3>
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in uploadResults.details.failed" :key="item.sku">
                      <td>{{ item.sku }}</td>
                      <td class="error--text">{{ item.error }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Back button -->
            <v-row justify="center" class="mt-8">
              <v-col cols="auto">
                <v-btn color="accent" large @click="$router.push('/listingSupervision')">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back to Listing Supervision
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import ProductsToFix from "../Platforms/ProductsToFix.vue";
import ProductsToUpload from "../Platforms/ProductsToUpload.vue";
import ProductsToTranslate from "../Platforms/ProductsToTranslate.vue";
import CategoryMapping from "../Platforms/CategoryMapping.vue";
import FillAspects from "../Platforms/FillAspects.vue";

export default {
  name: "ExportToPlatform",
  components: {
    ProductsToFix,
    ProductsToUpload,
    ProductsToTranslate,
    CategoryMapping,
    FillAspects,
  },
  props: {
    platform: Object,
    SKUArray: Array,
  },

  data: () => ({
    // Loading / error states
    loadingProducts: true,
    getProductsFailed: false,
    responseImg: "",
    responseMessage: "",

    // Products
    products: [],
    SKUToUpload: [],

    // Stepper visibility flags
    productsToFix: false,
    productsToUpload: false,
    productsToTranslate: false,
    showCategoryMapping: false,

    // Aspect checking (step 4.5)
    checkingAspects: false,
    showFillAspects: false,
    aspectsReport: [],

    // Upload (step 5)
    uploading: false,
    uploadResults: null,

    // Misc
    stepper: 1,
    isEditing: true,
  }),

  beforeMount() {
    if (!this.platform) {
      this.$router.push("/listingSupervision");
    }
    window.addEventListener("beforeunload", this.preventReload);
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventReload);
  },

  async mounted() {
    await this.getProductsForUpload(false);
  },

  methods: {
    preventReload(event) {
      if (!this.isEditing) return;
      event.preventDefault();
      event.returnValue = "";
    },

    // Gets products for platform upload
    // upload=false → products that need fixing
    // upload=true  → products ready to upload
    async getProductsForUpload(upload) {
      this.loadingProducts = true;
      this.getProductsFailed = false;
      this.productsToFix = false;
      this.productsToUpload = false;

      const body = {
        upload,
        platform: this.platform.platform,
        skus: this.SKUArray,
      };

      if (this.platform) {
        try {
          const response = await this.$store.dispatch("getProductsForPlatformUpload", body);
          console.log(response);
          this.products = response.data;
          this.loadingProducts = false;

          if (upload) {
            this.stepper = 2;
            this.productsToUpload = true;
            this.SKUToUpload = await this.getSKUArray(this.products);
          } else {
            this.stepper = 1;
            this.productsToFix = true;
          }
        } catch (error) {
          console.log(error);
          this.loadingProducts = false;
          this.products = [];

          if (error.response.status === 404) {
            if (upload) {
              this.stepper = 2;
              this.productsToUpload = true;
              this.SKUToUpload = await this.getSKUArray(this.products);
            } else {
              this.stepper = 1;
              this.productsToFix = true;
            }
          } else {
            this.responseImg = "feeling_blue.svg";
            this.responseMessage = error.response.data
              ? error.response.data
              : "Something went wrong when trying to get products...";
            this.getProductsFailed = true;
          }
        }
      }
    },

    async getSKUArray(array) {
      return array.map(product => product.SKU);
    },

    async getProductsToTranslate() {
      this.stepper = 3;
      this.productsToTranslate = true;
    },

    // Called from ProductsToTranslate @translatedSuccessfully
    async translatedSuccessfully() {
      if (this.platform.platform.includes("ebay")) {
        this.stepper = 4;
        this.showCategoryMapping = true;
      } else {
        await this.uploadProducts();
      }
    },

    // Called from CategoryMapping @goToUpload
    async goToAspectCheck() {
      this.stepper = 4.5;
      this.checkingAspects = true;
      this.showFillAspects = false;

      try {
        const response = await this.$store.dispatch("checkAspects", {
          skus: this.SKUToUpload,
          platform: this.platform.platform,
        });

        this.aspectsReport = response.data;
        const anyMissing = this.aspectsReport.some(r => r.needsInput);

        if (anyMissing) {
          this.checkingAspects = false;
          this.showFillAspects = true;
        } else {
          // All good — collect auto-fills and upload directly
          const autoOverrides = {};
          for (const item of this.aspectsReport) {
            if (Object.keys(item.autoFilled).length > 0) {
              autoOverrides[item.sku] = item.autoFilled;
            }
          }
          this.checkingAspects = false;
          await this.uploadProducts(autoOverrides);
        }
      } catch (e) {
        console.error("checkAspects failed:", e);
        this.checkingAspects = false;
        // Fall through to upload anyway
        await this.uploadProducts({});
      }
    },

    // Called from FillAspects @confirmed
    async onAspectsConfirmed(aspectOverrides) {
      await this.uploadProducts(aspectOverrides);
    },

    // Final upload
    async uploadProducts(aspectOverrides = {}) {
      this.stepper = 5;
      this.uploading = true;
      this.uploadResults = null;

      const body = {
        platform: this.platform.platform,
        skus: this.SKUToUpload,
        aspectOverrides,
      };

      try {
        const response = await this.$store.dispatch("uploadProducts", body);
        this.uploadResults = response.data;
      } catch (error) {
        this.uploadResults = {
          success: 0,
          failed: this.SKUToUpload.length,
          details: {
            success: [],
            failed: this.SKUToUpload.map(sku => ({
              sku,
              error: error?.response?.data?.message || "Upload failed",
            })),
          },
        };
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>